import { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { CalendarIcon } from "components/Icons";
import { cn } from "tools/ui";
import { ModernCalendar } from "componentsv2/form/DateCalendar";

export default function Date2Filter({ name, value = "", onChange, placeholder = "dd/mm/aa" }) {
  const intl = useIntl();
  const placeholderText =
    typeof placeholder === "string"
      ? intl.formatMessage({ id: placeholder, defaultMessage: placeholder })
      : String(placeholder);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  function handleInputChange(e) {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let formatted = "";
    for (let i = 0; i < digits.length; i++) {
      if (i === 2 || i === 4) formatted += "/";
      formatted += digits[i];
    }
    setInputValue(formatted);
  }

  function handleInputBlur() {
    if (!inputValue.trim()) {
      onChange(name, null);
      return;
    }
    const parts = inputValue.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = 2000 + parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        const str = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        onChange(name, str);
        return;
      }
    }
    onChange(name, null);
  }

  function handleSelect(dateStr) {
    onChange(name, dateStr);
    setIsOpen(false);
    if (dateStr) {
      const [y, m, d] = dateStr.split("-");
      setInputValue(`${d}/${m}/${y.slice(-2)}`);
    } else {
      setInputValue("");
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="flex px-3 h-10.5 justify-between shadow-sm border border-gray-200 rounded-xl bg-white">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder={placeholderText}
          className={cn("w-full outline-none bg-transparent text-sm")}
        />
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="absolute right-2 top-3 cursor-pointer"
        >
          <CalendarIcon className="text-neutral-300 size-5" />
        </button>
      </div>
      {isOpen && (
        <ModernCalendar
          selected={value || null}
          onSelect={handleSelect}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
